'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { MessageSquare } from 'lucide-react';
import usePublicContacts, { phoneTel } from '@/hooks/usePublicContacts';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * Floating WhatsApp + enquiry buttons, bottom-right on every marketing page.
 * The enquiry button opens /contact, which hosts the demo-request/enquiry form.
 *
 * Mounted once inside the landing Footer, so it appears exactly where the
 * marketing chrome does and never on a school's themed pages (which render
 * their own footer). The WhatsApp number is the Support Phone from
 * SuperAdmin → Settings (Business Profile), via the same shared
 * usePublicContacts cache the Footer's contact column reads — changing the
 * number there changes this button everywhere, no deploy needed.
 */
export default function FloatingSupport() {
  const contacts = usePublicContacts();
  const { t } = useLanguage();
  const pathname = usePathname();

  // wa.me wants international digits with no `+`: '9887664666' → '919887664666'.
  const waPhone = phoneTel(contacts.supportPhone).replace(/\D/g, '');
  const waHref = `https://wa.me/${waPhone}?text=${encodeURIComponent(t('footer.floatingWhatsAppMsg'))}`;

  // On /contact the enquiry button would link to the page it's already on.
  const onContactPage = pathname === '/contact' || pathname?.startsWith('/contact/');

  const whatsappTooltip = t('footer.floatingWhatsApp');
  const enquiryTooltip = t('footer.floatingSupport');

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-center gap-3 md:bottom-8 md:right-8">
      {/* Enquiry — the demo-request / enquiry form on /contact */}
      {!onContactPage && (
        <Link
          href="/contact"
          aria-label={enquiryTooltip}
          title={enquiryTooltip}
          className="group relative flex h-14 w-14 items-center justify-center rounded-full bg-[#3D5EE1] text-white shadow-lg shadow-blue-500/40 transition-transform duration-200 hover:scale-110 hover:bg-[#2A48C9] focus:outline-none focus:ring-4 focus:ring-blue-400/60"
        >
          <MessageSquare size={26} aria-hidden="true" />

          {/* Tooltip — desktop only */}
          <span className="pointer-events-none absolute right-full mr-3 hidden whitespace-nowrap rounded-md bg-gray-900/90 px-3 py-1.5 text-xs font-medium text-white opacity-0 shadow-md transition-opacity duration-200 group-hover:opacity-100 md:block">
            {enquiryTooltip}
          </span>
        </Link>
      )}

      {/* WhatsApp — chat with the support phone from Business Settings */}
      <a
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={whatsappTooltip}
        title={whatsappTooltip}
        className="group relative flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg shadow-green-500/40 transition-transform duration-200 hover:scale-110 focus:outline-none focus:ring-4 focus:ring-green-400/60"
      >
        {/* WhatsApp glyph (Simple Icons - CC0) */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="relative h-8 w-8"
          aria-hidden="true"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884a9.825 9.825 0 0 1 6.988 2.901 9.825 9.825 0 0 1 2.892 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
        </svg>

        {/* Tooltip — desktop only */}
        <span className="pointer-events-none absolute right-full mr-3 hidden whitespace-nowrap rounded-md bg-gray-900/90 px-3 py-1.5 text-xs font-medium text-white opacity-0 shadow-md transition-opacity duration-200 group-hover:opacity-100 md:block">
          {whatsappTooltip}
        </span>
      </a>
    </div>
  );
}
